import type { JobStatusCount, JobGraphicsStatusCount } from "../../interfaces/apiInterfaces/jobs";
import type { JobStatus } from "../../interfaces/apiInterfaces/enums/job.enums";
import type { UploadStatus } from "../../interfaces/componentInterfaces/table";
import { useResources } from "../../hooks/useResources";

export const renderUploadStatus = (status: UploadStatus) => {
  if (status.state === "processing") {
    return <span className="status-text processing">Processing</span>;
  }

  const tone = status.state ?? "neutral";

  return (
    <span className={`status-text ${tone}`}>
      <span className="grey-text  ">{status.total} Total | </span>
      <span className="green-text">{status.success} (✓)</span>
      <span className="grey-text"> | </span>
      <span className="red-text">{status.failed} (✕)</span>
    </span>
  );
};

/**
 * Format UTC datetime string to local readable format
 */
export const formatDateTime = (utcDateString: string): string => {
  const { getResourceValue } = useResources();
  
  try {
    const date = new Date(utcDateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const fileDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    if (fileDate.getTime() === today.getTime()) {
      return `${getResourceValue("today", "Today")}, ${timeStr}`;
    } else if (fileDate.getTime() === yesterday.getTime()) {
      return `${getResourceValue("yesterday", "Yesterday")}, ${timeStr}`;
    } else {
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return utcDateString;
  }
};

/**
 * Render job status (Uploading Status column)
 */
export const renderJobStatus = (jobStatus: JobStatus, statusCount: JobStatusCount | null) => {
  const { getResourceValue } = useResources();
  
  // If job is processing, show "Processing..."
  if (jobStatus === 'processing') {
    return <span className="status-text processing">{getResourceValue("processing", "Processing...")}</span>;
  }

  // If job status is failed, show "Failed"
  if (jobStatus === 'failed') {
    return <span className="status-text red-text">{getResourceValue("failed", "Failed")}</span>;
  }

  // If status count is null, show empty string
  if (statusCount === null) {
    return <span className="status-text"></span>;
  }

  const { total_count, success_count, failed_count } = statusCount;

  return (
    <span className="status-text">
      <span className="grey-text">{total_count} {getResourceValue("total", "Total")} | </span>
      <span className="green-text">{success_count} (✓)</span>
      <span className="grey-text"> | </span>
      <span className="red-text">{failed_count} (✕)</span>
    </span>
  );
};

/**
 * Render file status (File Status column)
 */
export const renderFileStatus = (fileStatus: string) => {
  const { getResourceValue } = useResources();
  const status = fileStatus?.toLowerCase();
  
  switch (status) {
    case 'processing':
      return <span className="status-text processing">{getResourceValue("processing", "Processing")}</span>;
    case 'completed':
      return <span className="status-text green-text">{getResourceValue("completed", "Completed")}</span>;
    case 'failed':
      return <span className="status-text red-text">{getResourceValue("failed", "Failed")}</span>;
    default:
      return <span className="status-text">{fileStatus}</span>;
  }
};

/**
 * Render graphics status (Graphics Status column)
 */
export const renderGraphicsStatus = (jobStatus: JobStatus, graphicsCount: JobGraphicsStatusCount | null) => {
  const { getResourceValue } = useResources();
  
  // If job is processing, show "Processing..."
  if (jobStatus === 'processing') {
    return <span className="status-text processing">{getResourceValue("processing", "Processing...")}</span>;
  }

  // If graphics count is null, show empty string
  if (graphicsCount === null) {
    return <span className="status-text"></span>;
  }

  const { total_count, success_count, failed_count } = graphicsCount;

  return (
    <span className="status-text">
      <span className="grey-text">{total_count} {getResourceValue("total", "Total")} | </span>
      <span className="green-text">{success_count} (✓)</span>
      <span className="grey-text"> | </span>
      <span className="red-text">{failed_count} (✕)</span>
    </span>
  );
};
